import { useState } from "react";
import { ArrowLeft, MessageSquare, Users, KeyRound, RefreshCw, LogOut, Bot } from "lucide-react";
import SeoDashboard from "@/components/SeoDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [key, setKey] = useState(localStorage.getItem("xs-admin-key") || "");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("chats");
  const [chats, setChats] = useState([]);
  const [leads, setLeads] = useState([]);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Key": k };
      const [rc, rl] = await Promise.all([
        fetch(`${API}/admin/chats`, { headers }),
        fetch(`${API}/leads`, { headers }),
      ]);
      if (rc.status === 401 || rl.status === 401) {
        setError("Chiave non valida");
        setAuthed(false);
        return;
      }
      setChats(await rc.json());
      setLeads(await rl.json());
      localStorage.setItem("xs-admin-key", k);
      setAuthed(true);
    } catch {
      setError("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("xs-admin-key");
    setAuthed(false);
    setKey("");
  };

  const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="bg-void text-white font-body min-h-screen">
      <div className="grain-overlay" />
      <div className="max-w-4xl mx-auto px-6 py-16">
        <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10">
          <ArrowLeft size={16} /> Torna alla home
        </a>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">
          Area <span className="neon-text">Riservata</span>
        </h1>
        <p className="text-sm text-white/50 mb-10">Storico chat IA e richieste dei visitatori.</p>

        {!authed ? (
          <form
            onSubmit={(e) => { e.preventDefault(); load(key); }}
            className="glass-panel rounded-2xl p-8 max-w-md flex flex-col gap-4"
            data-testid="admin-login-form"
          >
            <div className="flex items-center gap-3 text-neon-cyan">
              <KeyRound size={20} />
              <p className="font-semibold text-white">Inserisci la chiave admin</p>
            </div>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Chiave admin"
              data-testid="admin-key-input"
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-neon-cyan/60"
            />
            {error && <p className="text-xs text-neon-magenta" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-6 py-3 text-sm font-semibold text-void hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-8 flex-wrap">
              <button
                onClick={() => setTab("chats")}
                data-testid="admin-tab-chats"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${tab === "chats" ? "border-neon-cyan/50 text-neon-cyan bg-neon-cyan/10" : "border-white/10 text-white/60 hover:text-white"}`}
              >
                <MessageSquare size={15} /> Chat IA ({chats.length})
              </button>
              <button
                onClick={() => setTab("leads")}
                data-testid="admin-tab-leads"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${tab === "leads" ? "border-neon-cyan/50 text-neon-cyan bg-neon-cyan/10" : "border-white/10 text-white/60 hover:text-white"}`}
              >
                <Users size={15} /> Richieste ({leads.length})
              </button>
              <button
                onClick={() => setTab("seo")}
                data-testid="admin-tab-seo"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${tab === "seo" ? "border-neon-cyan/50 text-neon-cyan bg-neon-cyan/10" : "border-white/10 text-white/60 hover:text-white"}`}
              >
                <Bot size={15} /> Agente SEO
              </button>
              <button onClick={() => load(key)} data-testid="admin-refresh-button" className="flex items-center gap-2 text-sm text-white/50 hover:text-neon-cyan transition-colors ml-auto">
                <RefreshCw size={14} /> Aggiorna
              </button>
              <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-sm text-white/50 hover:text-neon-magenta transition-colors">
                <LogOut size={14} /> Esci
              </button>
            </div>

            {tab === "seo" && <SeoDashboard adminKey={key} />}

            {tab === "chats" && (
              <div className="flex flex-col gap-6" data-testid="admin-chats-list">
                {chats.length === 0 && <p className="text-white/40 text-sm">Nessuna conversazione ancora.</p>}
                {chats.map((s) => (
                  <div key={s.session_id} className="glass-panel rounded-2xl p-6" data-testid={`chat-session-${s.session_id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <p className="hud-label text-[10px] uppercase text-white/40">Sessione {s.session_id.slice(0, 8)}</p>
                      <p className="text-xs text-white/40">{fmt(s.last)}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {s.messages.map((m, i) => (
                        <div
                          key={i}
                          className={m.role === "user"
                            ? "self-end bg-neon-cyan/10 border border-neon-cyan/25 rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%]"
                            : "self-start bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-white/75 max-w-[85%]"}
                        >
                          {m.text}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "leads" && (
              <div className="flex flex-col gap-6" data-testid="admin-leads-list">
                {leads.length === 0 && <p className="text-white/40 text-sm">Nessuna richiesta ancora.</p>}
                {leads.map((l) => (
                  <div key={l.id} className="glass-panel rounded-2xl p-6" data-testid={`lead-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <p className="font-display font-bold">{l.name}</p>
                        <p className="text-xs text-neon-cyan mt-1">{l.topic}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/40">{fmt(l.created_at)}</p>
                        <p className="text-[10px] hud-label uppercase text-white/30 mt-1">{l.source}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-white/60 mb-3">
                      {l.azienda && <p><span className="text-white/35">Azienda:</span> {l.azienda}</p>}
                      {l.settore && <p><span className="text-white/35">Settore:</span> {l.settore}</p>}
                      {l.luogo && <p><span className="text-white/35">Luogo:</span> {l.luogo}</p>}
                      {(l.contatto || l.email) && <p><span className="text-white/35">Contatto:</span> {l.contatto || l.email}</p>}
                    </div>
                    {l.message && <p className="text-sm text-white/70 border-l-2 border-white/15 pl-3 mb-3">{l.message}</p>}
                    {l.draft_reply && (
                      <div className="mt-4 bg-white/5 border border-neon-violet/30 rounded-xl p-4" data-testid={`lead-draft-${l.id}`}>
                        <p className="hud-label text-[10px] uppercase text-neon-violet mb-2">Bozza risposta — Claude Opus</p>
                        <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{l.draft_reply.replace(/\*\*/g, "")}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
